import { useEffect, useMemo, useState } from "react";
import { Reveal, Stagger, staggerItem } from "@/components/site/Reveal";
import { SectionLabel } from "@/components/site/SectionLabel";
import { PageMeta } from "@/components/site/PageMeta";
import { getSection, listItems } from "@/lib/api";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { GraduationCap, Mic2, Award, BookOpen, Building2 } from "lucide-react";

// Normalise institute names into stable keys, preferring the exact strings the
// user asked us to group by. Everything unknown falls back to "Other".
function institutionKey(rawInstitute = "") {
    const s = rawInstitute.toLowerCase();
    if (s.includes("nlu") || s.includes("national law")) return "nlu";
    if (s.includes("presidency")) return "presidency";
    return "other";
}
const INSTITUTIONS = [
    {
        key: "nlu",
        name: "National Law University, Jodhpur",
        role: "Academic Fellow",
        blurb: "Advanced corporate law and securities regulation at the LL.M. level.",
    },
    {
        key: "presidency",
        name: "Presidency University",
        role: "Faculty",
        blurb: "Foundational corporate governance, securities regulation, and M&A at the LL.B. level.",
    },
    {
        key: "other",
        name: "Other affiliations",
        role: "Guest / Adjunct",
        blurb: "Additional teaching engagements across institutions.",
    },
];

// Level chip tone — normalise UG/PG/LL.B/LL.M into a compact pill label
function levelTone(level = "") {
    const s = level.toUpperCase();
    if (s.includes("LL.M") || s.includes("PG") || s.includes("POST")) {
        return { label: level || "PG", cls: "bg-forest text-paper" };
    }
    if (s.includes("LL.B") || s.includes("UG") || s.includes("UNDER")) {
        return { label: level || "UG", cls: "bg-brass/20 text-forest border border-forest/25" };
    }
    return { label: level || "Course", cls: "bg-secondary text-foreground border border-border/60" };
}

export default function TeachingPage() {
    const [teaching, setTeaching] = useState(null);
    const [courses, setCourses] = useState([]);

    useEffect(() => {
        getSection("teaching").then((r) => setTeaching(r.data || {}));
        listItems("courses").then(setCourses);
    }, []);

    const t = teaching || {};

    // Group courses by institution
    const groupedCourses = useMemo(() => {
        const map = new Map();
        courses.forEach((c) => {
            const key = institutionKey(c.institute);
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(c);
        });
        return map;
    }, [courses]);

    // Only keep sections that actually have data
    const institutionsWithCourses = INSTITUTIONS.filter((inst) => (groupedCourses.get(inst.key) || []).length > 0);

    return (
        <>
            <PageMeta title="Teaching — Aditya Baheti" description="Teaching philosophy, courses, and guest lectures." path="/teaching" />

            {/* ============================= HERO ================================= */}
            <section className="relative overflow-hidden">
                <div className="absolute inset-0 hero-glow" aria-hidden />
                <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 pb-10">
                    <SectionLabel label="Teaching" />
                    <Reveal>
                        <h1 className="font-display text-5xl sm:text-7xl lg:text-8xl leading-[0.98] tracking-[-0.02em] max-w-4xl">
                            Teaching as <span className="text-claret">translation</span>.
                        </h1>
                    </Reveal>
                    <Reveal delay={0.05}>
                        <p className="mt-6 max-w-[62ch] text-base sm:text-lg text-muted-foreground">
                            Corporate law is dense — but the ideas underneath it are practical. My teaching turns
                            doctrine into instinct that students can carry into the room.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* ========================= PHILOSOPHY =============================== */}
            <section className="relative overflow-hidden">
                <div className="claret-glow absolute inset-0" aria-hidden />
                <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 grid lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-4">
                        <SectionLabel label="Philosophy" />
                        <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.24em] text-muted-foreground">
                            The commitment that anchors the classroom.
                        </p>
                    </div>
                    <div className="lg:col-span-8">
                        <Reveal>
                            <blockquote className="pl-5 border-l-2 border-forest">
                                <GraduationCap className="h-6 w-6 text-forest mb-3" />
                                <p className="font-display text-2xl sm:text-3xl leading-snug tracking-tight">
                                    “{t.philosophy}”
                                </p>
                            </blockquote>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* ==================== COURSES BY INSTITUTION ======================== */}
            <section id="courses" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 scroll-mt-24">
                <SectionLabel label="Courses" />
                <Reveal>
                    <h2 className="font-display text-4xl sm:text-5xl tracking-tight max-w-3xl">
                        Courses taught, by institution.
                    </h2>
                </Reveal>
                <Reveal delay={0.05}>
                    <p className="mt-3 max-w-[62ch] text-muted-foreground">
                        Grouped by the institution where the course was offered. Each card lists the course name,
                        level, and a short description.
                    </p>
                </Reveal>

                <div className="mt-12 space-y-14" data-testid="teaching-institutions">
                    {institutionsWithCourses.map((inst) => {
                        const list = groupedCourses.get(inst.key) || [];
                        return (
                            <div key={inst.key} data-testid={`teaching-institution-${inst.key}`}>
                                <div className="flex items-baseline gap-4 mb-6">
                                    <div className="flex items-center gap-3">
                                        <span className="h-9 w-9 rounded-full grid place-items-center bg-forest/10 text-forest">
                                            <Building2 className="h-4 w-4" />
                                        </span>
                                        <div>
                                            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                                                {inst.role}
                                            </p>
                                            <p className="font-display text-2xl sm:text-3xl tracking-tight">
                                                {inst.name}
                                            </p>
                                        </div>
                                    </div>
                                    <span className="h-px flex-1 bg-border/70 hidden sm:block" />
                                    <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground hidden sm:inline">
                                        {list.length} {list.length === 1 ? "course" : "courses"}
                                    </span>
                                </div>
                                <p className="mb-6 max-w-[62ch] text-sm text-muted-foreground">{inst.blurb}</p>
                                <Stagger className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {list.map((c) => {
                                        const chip = levelTone(c.level);
                                        return (
                                            <motion.div key={c.id} variants={staggerItem}>
                                                <Card
                                                    className="h-full p-5 rounded-2xl border-border/70 bg-card hover:shadow-[0_20px_50px_-20px_hsl(var(--ink)/0.20)] transition-shadow"
                                                    data-testid="teaching-course-card"
                                                >
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest ${chip.cls}`}>
                                                            {chip.label}
                                                        </span>
                                                        <BookOpen className="ml-auto h-4 w-4 text-forest/60" />
                                                    </div>
                                                    <p className="mt-3 font-display text-xl leading-snug">{c.name}</p>
                                                    {c.description && (
                                                        <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                                                            {c.description}
                                                        </p>
                                                    )}
                                                </Card>
                                            </motion.div>
                                        );
                                    })}
                                </Stagger>
                            </div>
                        );
                    })}
                    {institutionsWithCourses.length === 0 && (
                        <p className="text-sm text-muted-foreground">No courses listed yet.</p>
                    )}
                </div>
            </section>

            {/* ==================== CASE STUDIES & MATERIALS ====================== */}
            <section id="materials" className="relative overflow-hidden bg-secondary/40 border-y border-border/70 py-16 sm:py-20 scroll-mt-24">
                <div className="claret-glow absolute inset-0" aria-hidden />
                <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <SectionLabel label="Case studies & materials" />
                    <Reveal>
                        <h2 className="font-display text-4xl sm:text-5xl tracking-tight max-w-3xl">
                            Additional teaching materials.
                        </h2>
                    </Reveal>

                    {(t.additional_courses || []).length > 0 ? (
                        <Stagger className="mt-10 grid md:grid-cols-2 gap-4">
                            {(t.additional_courses || []).map((a, i) => (
                                <motion.div key={i} variants={staggerItem}>
                                    <Card className="h-full p-5 rounded-2xl border-border/70 bg-card">
                                        <div className="flex items-center gap-2">
                                            <Award className="h-4 w-4 text-brass" />
                                            <span className="font-mono text-[10px] uppercase tracking-widest text-brass">
                                                Certificate
                                            </span>
                                        </div>
                                        <p className="mt-3 font-display text-xl leading-snug">{a.name}</p>
                                        {a.institute && (
                                            <p className="mt-1 text-xs font-mono uppercase tracking-widest text-muted-foreground">
                                                {a.institute}
                                            </p>
                                        )}
                                        {a.description && (
                                            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                                                {a.description}
                                            </p>
                                        )}
                                    </Card>
                                </motion.div>
                            ))}
                        </Stagger>
                    ) : (
                        <p className="mt-6 max-w-[62ch] text-sm text-muted-foreground">
                            Case studies, reading lists, and downloadable slide decks will be posted here as they are cleared for release.
                        </p>
                    )}
                </div>
            </section>

            {/* ==================== GUEST LECTURES ================================ */}
            {(t.guest_lectures || []).length > 0 && (
                <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
                    <SectionLabel label="Guest lectures" />
                    <Reveal>
                        <h2 className="font-display text-4xl sm:text-5xl tracking-tight max-w-3xl">
                            Invited talks and guest sessions.
                        </h2>
                    </Reveal>
                    <div className="mt-8 divide-y divide-border/70 border-y border-border/70">
                        {(t.guest_lectures || []).map((g, i) => (
                            <Reveal key={i} delay={i * 0.04}>
                                <div className="py-5 flex flex-wrap items-baseline justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                            <Mic2 className="h-4 w-4 text-forest" />
                                            <span className="font-mono text-[10px] uppercase tracking-widest text-forest">
                                                Guest lecture
                                            </span>
                                        </div>
                                        <p className="mt-2 font-display text-xl leading-snug">{g.title}</p>
                                        {g.host && (
                                            <p className="mt-1 font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                                                {g.host}
                                            </p>
                                        )}
                                    </div>
                                    {g.year && <p className="font-mono text-sm text-muted-foreground">{g.year}</p>}
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </section>
            )}
        </>
    );
}
